'use client';

import { usePathname, useRouter } from 'next/navigation';
import React from 'react';
import { Dropdown, DropdownProps } from 'react-bootstrap';
import Image from 'next/image';
import { BiCaretDown } from 'react-icons/bi';
import { locales } from '@/libs/i18n';
import { SupportedLanguages, useTranslation } from '@/hooks/useTranslation';

export const LanguageMapper = {
  'en-US': 'English',
  en: 'English',
  'fr-FR': 'Français',
  fr: 'Français',
} as { [key: string]: string };

export const lngFlagMapper = {
  'en-US': 'https://cdn.1cdn.app/application/UMD/24041507435389_US.png',
  en: 'https://cdn.1cdn.app/application/UMD/24041507435389_US.png',
  'fr-FR': 'https://cdn.1cdn.app/application/UMD/24041507435387_France.png',
  fr: 'https://cdn.1cdn.app/application/UMD/24041507435387_France.png',
} as { [key: string]: string };

interface LanguageChangeDropdownProps {
  placement?: DropdownProps['placement'];
  id: string;
  locale: SupportedLanguages;
}

const LanguageChangeDropdown = ({ placement = 'bottom', id, locale }: LanguageChangeDropdownProps) => {
  const router = useRouter();
  const pathName = usePathname();

  const selectHandler = (newLocale: string) => {
    if (!pathName) return `/${newLocale}`;

    const segments = pathName.split('/');
    if (segments.length < 2) return `/${newLocale}`;

    // Vérifie si le premier segment est une locale valide
    if (locales.includes(segments[1] as any)) {
      segments[1] = newLocale;
    } else {
      segments.splice(1, 0, newLocale);
    }

    return segments.join('/');
  };

  const handleClick = (url: string) => {
    router.push(url);
  };

  // eslint-disable-next-line react/display-name
  const CustomToggle = React.forwardRef<
    HTMLDivElement,
    { children: React.ReactNode; onClick: (e: React.MouseEvent) => void }
  >(({ children, onClick }, ref) => (
    <div
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
      className="lngSelectorWrapper tw-p-1 tw-rounded-full tw-bg-gray-800 tw-relative tw-flex tw-items-center"
    >
      <Image
        priority
        quality={100}
        src={lngFlagMapper[locale] ?? lngFlagMapper['en-US']}
        alt={locale}
        width={28}
        height={28}
        className="tw-rounded-full"
        title={`${locale} flag`}
      />
    </div>
  ));

  return (
    <Dropdown placement={placement}>
      <Dropdown.Toggle as={CustomToggle} id={id} />
      <Dropdown.Menu>
        {locales.map((l) => (
          <Dropdown.Item key={l} as={'span'}>
            <span
              onClick={() => {
                handleClick(selectHandler(l));
              }}
              className="d-block tw-cursor-pointer"
            >
              {LanguageMapper[l]}
            </span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default LanguageChangeDropdown;
